"use client";

import { useState, useRef, useCallback, memo } from "react";
import Link from "next/link";
import { Inbox, ChevronDown, ChevronLeft } from "lucide-react";
import {
  PRODUCT_NAV_SECTIONS,
  getSectionSub1List,
} from "@/lib/navigation/productCategories";

const PANEL_CLASS =
  "w-52 shrink-0 !rounded-[20px] overflow-hidden border border-white/10 shadow-2xl bg-[#1D1E20]/40 backdrop-blur-[22px]";

const TEXT_LINK =
  "!text-mute hover:!text-white !font-normal transition-colors";

function NavPanel({ children, className = "" }) {
  return <div className={`${PANEL_CLASS} ${className}`}>{children}</div>;
}

function PanelTitle({ href, children, onNavigate }) {
  if (!href) {
    return (
      <div className={`px-3 py-2.5 border-b border-[#383C42] text-xs ${TEXT_LINK}`}>
        {children}
      </div>
    );
  }

  return (
    <Link
      href={href}
      onClick={onNavigate}
      className={`block px-3 py-2.5 border-b border-[#383C42] text-xs font-medium ${TEXT_LINK}`}
    >
      {children}
    </Link>
  );
}

function NavRow({ label, isActive, hasChildren, onMouseEnter, href, onNavigate }) {
  const className = `group flex items-center justify-between gap-2 px-3 py-2.5 rounded-xl text-sm ${TEXT_LINK} ${
    isActive ? "bg-white/5" : ""
  }`;

  if (href) {
    return (
      <Link href={href} className={className} onClick={onNavigate} onMouseEnter={onMouseEnter}>
        <span className="flex-1">{label}</span>
        {hasChildren && (
          <ChevronLeft size={14} className="shrink-0 text-mute group-hover:text-white transition-colors" />
        )}
      </Link>
    );
  }

  return (
    <button
      type="button"
      className={`${className} w-full text-right`}
      onMouseEnter={onMouseEnter}
    >
      <span className="flex-1">{label}</span>
      {hasChildren && (
        <ChevronLeft size={14} className="shrink-0 text-mute group-hover:text-white transition-colors" />
      )}
    </button>
  );
}

const CategoryMenu = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeSectionId, setActiveSectionId] = useState(null);
  const [activeSub1Key, setActiveSub1Key] = useState(null);
  const timeoutRef = useRef(null);

  const activeSection = PRODUCT_NAV_SECTIONS.find((s) => s.id === activeSectionId);
  const sub1List = activeSection ? getSectionSub1List(activeSection) : [];
  const activeSub1 = sub1List.find((entry) => entry.key === activeSub1Key);
  const showLevel2 = Boolean(activeSection?.sub1?.length);
  const level3Items = activeSub1?.sub2 ?? [];
  const showLevel3 = level3Items.length > 0;

  const closeMenu = useCallback(() => {
    setIsOpen(false);
    setActiveSectionId(null);
    setActiveSub1Key(null);
  }, []);

  const handleMouseEnter = useCallback(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setIsOpen(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    timeoutRef.current = setTimeout(closeMenu, 120);
  }, [closeMenu]);

  const handleSectionEnter = useCallback((sectionId) => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setActiveSectionId(sectionId);
    setActiveSub1Key(null);
  }, []);

  const handleSub1Enter = useCallback((sub1Key) => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setActiveSub1Key(sub1Key);
  }, []);

  return (
    <div className="relative" onMouseLeave={handleMouseLeave}>
      <Link
        href="/categories"
        onMouseEnter={handleMouseEnter}
        onClick={closeMenu}
        className={`group flex items-center gap-x-5 cursor-pointer py-2 ${TEXT_LINK}`}
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <Inbox size={20} />
        <span className="text-sm font-medium">دسته بندی محصولات</span>
        <ChevronDown
          size={16}
          className={`text-mute group-hover:text-white transition-all duration-200 ${isOpen ? "rotate-180" : ""}`}
        />
      </Link>

      {isOpen && (
        <div
          onMouseEnter={handleMouseEnter}
          className="absolute top-full right-0 mt-1 z-[9999] flex items-start gap-2 animate-in fade-in slide-in-from-top-1 duration-200"
        >
          <NavPanel>
            <div className="p-2">
              {PRODUCT_NAV_SECTIONS.map((section) => (
                <NavRow
                  key={section.id}
                  label={section.title}
                  href={section.href}
                  isActive={section.id === activeSectionId}
                  hasChildren={(section.sub1?.length ?? 0) > 0}
                  onMouseEnter={() => handleSectionEnter(section.id)}
                  onNavigate={closeMenu}
                />
              ))}
            </div>
          </NavPanel>

          {showLevel2 && (
            <NavPanel className="animate-in fade-in slide-in-from-right-2 duration-150">
              <PanelTitle href={activeSection.href} onNavigate={closeMenu}>
                {activeSection.title}
              </PanelTitle>
              <div className="p-2">
                {sub1List.map((sub1, index) => (
                  <NavRow
                    key={sub1.key ?? `${activeSection.id}-sub1-${index}`}
                    label={sub1.title}
                    href={sub1.href}
                    isActive={sub1.key === activeSub1Key}
                    hasChildren={(sub1.sub2?.length ?? 0) > 0}
                    onMouseEnter={() => handleSub1Enter(sub1.key)}
                    onNavigate={closeMenu}
                  />
                ))}
              </div>
            </NavPanel>
          )}

          {showLevel3 && (
            <NavPanel className="w-60 animate-in fade-in slide-in-from-right-2 duration-150">
              <PanelTitle href={activeSub1.href} onNavigate={closeMenu}>
                {activeSub1.title}
              </PanelTitle>
              <div className="max-h-[360px] overflow-y-auto custom-scrollbar-dark p-2">
                {level3Items.map((item, index) => {
                  const itemKey =
                    item.href ?? item.slug ?? `${activeSub1.key}-sub2-${index}`;
                  const itemClass = `block px-3 py-2.5 rounded-xl text-sm ${TEXT_LINK} hover:bg-white/5`;

                  if (!item.href) {
                    return (
                      <span key={itemKey} className={itemClass}>
                        {item.title}
                      </span>
                    );
                  }

                  return (
                    <Link
                      key={itemKey}
                      href={item.href}
                      className={itemClass}
                      onClick={closeMenu}
                    >
                      {item.title}
                    </Link>
                  );
                })}
              </div>
            </NavPanel>
          )}
        </div>
      )}
    </div>
  );
};

export default memo(CategoryMenu);
